import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, ClipboardList, Mic, MicOff, ShoppingCart, Sparkles, Wallet } from 'lucide-react';
import { api, newIdempotencyKey } from '../../lib/api.js';
import { moneyToInput, parseMoneyInput } from '../../lib/money.js';
import { useHpath, useSession } from '../../app/session.jsx';
import { Modal, ModalBody, ModalFooter } from '../../components/Modal.jsx';
import { useToast } from '../../components/Toast.jsx';
import { Button, Input, Segmented, Select, Textarea } from '../../components/ui.jsx';

const TYPES = [
  { value: 'task', label: 'Tarefa', icon: ClipboardList },
  { value: 'shopping', label: 'Compra', icon: ShoppingCart },
  { value: 'expense', label: 'Conta', icon: Wallet },
  { value: 'event', label: 'Compromisso', icon: CalendarDays },
];
const EXAMPLES = [
  'Comprar leite e pão',
  'Limpar o banheiro sábado',
  'Conta de internet de 120 reais vence dia 15',
  'Dentista amanhã às 14h',
];
const MISSING_LABEL = {
  amount: 'o valor',
  dueDate: 'o vencimento',
  date: 'a data',
  items: 'o que comprar',
  text: 'o texto',
};

function getRecognition() {
  const SR = globalThis.SpeechRecognition ?? globalThis.webkitSpeechRecognition;
  return SR ? new SR() : null;
}

function emptyFields(type, today) {
  return {
    task: { title: '', dueDate: '', dueTime: '', assigneeId: '' },
    shopping: { itemsText: '', listId: '' },
    expense: { description: '', amount: '', dueDate: '', paidOn: '', status: 'pending' },
    event: { title: '', date: today, startTime: '', endTime: '', allDay: false },
  }[type];
}

/** Converte a proposta do servidor em campos editáveis do tipo escolhido. */
/** Estado comparável da proposta, para detectar correção manual antes de salvar. */
function snapshot(type, fields) {
  return JSON.stringify({ type, fields });
}

function fromProposal(p, type, text, today) {
  const f = p?.fields ?? {};
  const title = f.title ?? f.description ?? (f.items ?? []).map((i) => i.name).join(', ') ?? text;
  switch (type) {
    case 'task':
      return {
        title: title || text,
        dueDate: f.dueDate ?? f.date ?? '',
        dueTime: f.dueTime ?? f.startTime ?? '',
        assigneeId: '',
      };
    case 'shopping':
      return {
        itemsText: f.items?.length
          ? f.items.map((i) => [i.quantity, i.unit, i.name].filter(Boolean).join(' ')).join('\n')
          : title || text,
        listId: p?.context?.shoppingLists?.[0]?.id ?? '',
      };
    case 'expense':
      return {
        description: f.description ?? title ?? text,
        amount: f.amount ? moneyToInput(f.amount) : '',
        dueDate: f.dueDate ?? '',
        paidOn: f.paidOn ?? '',
        status: f.paidOn ? 'paid' : 'pending',
      };
    default:
      return {
        title: title || text,
        date: f.date ?? f.dueDate ?? today,
        startTime: f.startTime ?? f.dueTime ?? '',
        endTime: '',
        allDay: f.allDay ?? !(f.startTime ?? f.dueTime),
      };
  }
}

function parseItemsText(text) {
  return text
    .split(/\n|,/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((raw) => {
      const m = /^(\d+(?:[.,]\d+)?)\s*(kg|g|l|ml|un|pct|cx|lata|latas|dz)?\s+(.+)$/i.exec(raw);
      return m ? { name: m[3], quantity: m[1].replace(',', '.'), unit: m[2] ?? null } : { name: raw };
    });
}

function addHour(hhmm) {
  const [h, m] = hhmm.split(':').map(Number);
  return h >= 23 ? '23:59' : `${String(h + 1).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Captura rápida: texto ou voz -> proposta editável -> confirmação. Nada é salvo sem confirmar. */
export function CaptureSheet({ preset, onClose }) {
  return (
    <Modal open={preset !== null} onClose={onClose} title="Adicionar rápido" raw>
      <CaptureBody preset={preset ?? {}} onClose={onClose} />
    </Modal>
  );
}

function CaptureBody({ preset, onClose }) {
  const { today, members, user, householdId } = useSession();
  const hp = useHpath();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  // Vindo da caixa de entrada, o texto e a proposta já chegam prontos: abre direto na
  // revisão, sem repetir a interpretação que já rodou na chegada.
  const presetType = preset.proposal?.type ?? preset.type ?? 'task';
  const [text, setText] = useState(preset.text ?? '');
  const [proposal, setProposal] = useState(preset.proposal ?? null);
  const [type, setType] = useState(presetType);
  const [fields, setFields] = useState(() =>
    preset.proposal
      ? fromProposal(preset.proposal, presetType, preset.text ?? '', today)
      : emptyFields(presetType, today),
  );
  const [errors, setErrors] = useState({});
  const [stage, setStage] = useState(preset.proposal || preset.type ? 'review' : 'input');
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const keyRef = useRef(null);
  // Como a proposta estava quando apareceu na tela. Comparar com o que foi salvo diz se a
  // interpretação acertou — é a medida que mostra se capturar sem digitar cumpriu a promessa.
  const baselineRef = useRef(null);
  baselineRef.current ??= snapshot(presetType, fields);
  const voiceSupported = Boolean(globalThis.SpeechRecognition ?? globalThis.webkitSpeechRecognition);

  const listsQ = useQuery({
    queryKey: [householdId, 'shopping', 'lists'],
    queryFn: () => api(hp('/shopping/lists')),
  });
  const lists = listsQ.data?.items ?? [];
  const listId = fields.listId || lists[0]?.id || '';

  const set = (k, v) => {
    setFields((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const interpret = async (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const p = await api(hp('/capture/parse'), { method: 'POST', body: { text } });
      const t = p.type ?? 'task';
      const next = fromProposal(p, t, text, today);
      setProposal(p);
      setType(t);
      setFields(next);
      baselineRef.current = snapshot(t, next);
      setErrors({});
      setStage('review');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleVoice = () => {
    if (listening) {
      recRef.current?.stop();
      return;
    }
    const rec = getRecognition();
    if (!rec) return;
    rec.lang = 'pt-BR';
    rec.interimResults = true;
    rec.continuous = false;
    rec.onresult = (ev) => setText([...ev.results].map((r) => r[0].transcript).join(' '));
    rec.onerror = (ev) => {
      if (ev.error === 'not-allowed')
        toast.error('Permita o uso do microfone para ditar, ou digite o texto.');
      else if (ev.error !== 'no-speech' && ev.error !== 'aborted')
        toast.error('Não foi possível usar o microfone agora. Digite o texto.');
    };
    rec.onend = () => setListening(false);
    recRef.current = rec;
    setListening(true);
    rec.start();
  };

  const changeType = (t) => {
    setType(t);
    setErrors({});
    setFields(proposal || text ? fromProposal(proposal, t, text, today) : emptyFields(t, today));
  };

  const save = async () => {
    const errs = {};
    let request;
    if (type === 'task') {
      if (!fields.title.trim()) errs.title = 'Informe o que precisa ser feito.';
      request = {
        path: '/tasks',
        body: {
          title: fields.title,
          dueDate: fields.dueDate || null,
          dueTime: fields.dueTime || null,
          assigneeId: fields.assigneeId || null,
        },
      };
    } else if (type === 'shopping') {
      const items = parseItemsText(fields.itemsText);
      if (!items.length) errs.itemsText = 'Informe pelo menos um item.';
      if (!listId) errs.listId = 'Escolha a lista.';
      request = { path: `/shopping/lists/${listId}/items/bulk`, body: { items, source: 'capture' } };
    } else if (type === 'expense') {
      const amount = parseMoneyInput(fields.amount);
      if (!fields.description.trim()) errs.description = 'Descreva a conta.';
      if (!amount || amount === '0.00') errs.amount = 'Informe um valor válido, como 120,50.';
      if (fields.status === 'pending' && !fields.dueDate) errs.dueDate = 'Informe o vencimento.';
      request = {
        path: '/finance/entries',
        body: {
          kind: 'expense',
          description: fields.description,
          amount,
          dueDate: fields.status === 'pending' ? fields.dueDate || null : null,
          paidOn: fields.status === 'paid' ? fields.paidOn || today : null,
          visibility: 'household',
        },
      };
    } else {
      if (!fields.title.trim()) errs.title = 'Informe o compromisso.';
      if (!fields.date) errs.date = 'Informe a data.';
      if (!fields.allDay && !fields.startTime) errs.startTime = 'Informe o horário ou marque dia inteiro.';
      request = {
        path: '/agenda/events',
        body: {
          title: fields.title,
          allDay: fields.allDay,
          date: fields.date,
          startTime: fields.allDay ? null : fields.startTime,
          endTime: fields.allDay
            ? null
            : fields.endTime || (fields.startTime ? addHour(fields.startTime) : null),
        },
      };
    }
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setBusy(true);
    keyRef.current ??= newIdempotencyKey();
    try {
      const res = await api(hp(request.path), {
        method: 'POST',
        body: request.body,
        idempotencyKey: keyRef.current,
      });
      keyRef.current = null;
      // A caixa de entrada só sai da fila depois que o item existe de verdade no módulo.
      if (preset.inboxItemId) {
        const wasEdited = baselineRef.current !== snapshot(type, fields);
        await api(hp(`/inbox/${preset.inboxItemId}/resolve`), {
          method: 'POST',
          body: { resolvedAs: type, entityId: res?.id ?? null, wasEdited },
        }).catch(() => {
          // O registro foi criado; falhar aqui só deixa o item na caixa para decidir de novo.
          toast.error('Salvo, mas o item continua na caixa de entrada. Descarte-o por lá.');
        });
      }
      const labels = {
        task: 'Tarefa criada',
        shopping: 'Itens adicionados à lista',
        expense: 'Conta registrada',
        event: 'Compromisso agendado',
      };
      const to = {
        task: res?.id ? `/tarefas/${res.id}` : '/tarefas',
        shopping: `/compras/${listId}`,
        expense: res?.id ? `/financas/lancamentos/${res.id}` : '/financas',
        event: '/agenda',
      }[type];
      toast.success(labels[type], { action: { label: 'Ver', onClick: () => navigate(to) } });
      qc.invalidateQueries({ queryKey: [householdId] });
      onClose();
    } catch (err) {
      if (err.status && err.status < 500) keyRef.current = null;
      setErrors(err.fieldErrors ?? {});
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const missing = (proposal?.missing ?? []).filter((m) => m !== 'text');
  const dupNames = [...new Set((proposal?.context?.duplicates ?? []).map((d) => d.name))];

  if (stage === 'input') {
    return (
      <>
        <ModalBody>
          <form id="capture-form" className="form" onSubmit={interpret}>
            <label htmlFor="capture-text" className="field-label">
              Escreva ou dite do jeito que falaria
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                id="capture-text"
                className="input capture-input"
                placeholder="Ex.: comprar café e açúcar"
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={300}
                autoComplete="off"
                enterKeyHint="go"
                autoFocus
              />
              {voiceSupported && (
                <button
                  type="button"
                  className="icon-btn mic-btn"
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 12,
                    border: '1px solid var(--border-strong)',
                  }}
                  aria-pressed={listening}
                  aria-label={listening ? 'Parar de ouvir' : 'Ditar por voz'}
                  onClick={toggleVoice}
                >
                  {listening ? <MicOff aria-hidden="true" /> : <Mic aria-hidden="true" />}
                </button>
              )}
            </div>
            {listening && (
              <p className="small text-danger" role="status">
                Ouvindo… fale e depois toque para parar.
              </p>
            )}
            <div>
              <p className="tiny muted" style={{ marginBottom: 6 }}>
                Exemplos:
              </p>
              <div className="capture-examples">
                {EXAMPLES.map((ex) => (
                  <button key={ex} type="button" className="chip" onClick={() => setText(ex)}>
                    {ex}
                  </button>
                ))}
              </div>
            </div>
            <p className="tiny muted">
              Nada é salvo sem a sua confirmação. A interpretação usa regras simples, sem serviços externos.
            </p>
          </form>
        </ModalBody>
        <ModalFooter>
          <Button
            onClick={() => {
              setFields(emptyFields(type, today));
              setStage('review');
            }}
          >
            Preencher manualmente
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="capture-form"
            icon={Sparkles}
            loading={busy}
            disabled={!text.trim()}
          >
            Interpretar
          </Button>
        </ModalFooter>
      </>
    );
  }

  return (
    <>
      <ModalBody>
        <div className="form">
          <p className="small muted">Confira e ajuste antes de salvar.</p>
          <Segmented value={type} onChange={changeType} options={TYPES} label="Tipo" />
          {proposal && proposal.confidence === 'low' && (
            <div className="alert alert-warning" role="status">
              Não tenho certeza do tipo. Confira se é mesmo “
              {TYPES.find((t) => t.value === type)?.label.toLowerCase()}”.
            </div>
          )}
          {missing.length > 0 && proposal?.type === type && (
            <div className="alert" role="status">
              Falta informar {missing.map((m) => MISSING_LABEL[m] ?? m).join(' e ')}.
            </div>
          )}

          {type === 'task' && (
            <>
              <Input
                label="O que precisa ser feito"
                value={fields.title}
                onChange={(e) => set('title', e.target.value)}
                error={errors.title}
                maxLength={200}
              />
              <div className="form-row">
                <Input
                  label="Prazo"
                  optional
                  type="date"
                  value={fields.dueDate}
                  onChange={(e) => set('dueDate', e.target.value)}
                  error={errors.dueDate}
                />
                <Input
                  label="Horário"
                  optional
                  type="time"
                  value={fields.dueTime}
                  onChange={(e) => set('dueTime', e.target.value)}
                />
              </div>
              <Select
                label="Responsável"
                optional
                placeholder="Sem responsável (ou responsável da área)"
                options={members.map((m) => ({
                  value: m.id,
                  label: m.id === user?.id ? `${m.name} (eu)` : m.name,
                }))}
                value={fields.assigneeId}
                onChange={(e) => set('assigneeId', e.target.value)}
              />
            </>
          )}

          {type === 'shopping' && (
            <>
              <Textarea
                label="Itens (um por linha)"
                value={fields.itemsText}
                onChange={(e) => set('itemsText', e.target.value)}
                error={errors.itemsText}
                hint="Quantidade opcional no início: “2 kg arroz”."
              />
              {dupNames.length > 0 && (
                <div className="alert alert-warning">
                  Já estão pendentes em alguma lista: {dupNames.join(', ')}. Salve assim mesmo se quiser mais.
                </div>
              )}
              <Select
                label="Lista"
                options={lists.map((l) => ({ value: l.id, label: l.name }))}
                value={listId}
                onChange={(e) => set('listId', e.target.value)}
                error={errors.listId}
                placeholder={lists.length ? undefined : 'Carregando listas…'}
              />
            </>
          )}

          {type === 'expense' && (
            <>
              <Input
                label="Descrição"
                value={fields.description}
                onChange={(e) => set('description', e.target.value)}
                error={errors.description}
                maxLength={200}
              />
              <div className="form-row">
                <Input
                  label="Valor (R$)"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={fields.amount}
                  onChange={(e) => set('amount', e.target.value)}
                  error={errors.amount}
                />
                <Select
                  label="Situação"
                  options={[
                    { value: 'pending', label: 'A pagar' },
                    { value: 'paid', label: 'Já paga' },
                  ]}
                  value={fields.status}
                  onChange={(e) => set('status', e.target.value)}
                />
              </div>
              {fields.status === 'pending' ? (
                <Input
                  label="Vencimento"
                  type="date"
                  value={fields.dueDate}
                  onChange={(e) => set('dueDate', e.target.value)}
                  error={errors.dueDate}
                />
              ) : (
                <Input
                  label="Pago em"
                  type="date"
                  max={today}
                  value={fields.paidOn || today}
                  onChange={(e) => set('paidOn', e.target.value)}
                />
              )}
              <p className="tiny muted">
                Visível para todos do lar. Categoria e visibilidade podem ser ajustadas depois em Finanças.
              </p>
            </>
          )}

          {type === 'event' && (
            <>
              <Input
                label="Compromisso"
                value={fields.title}
                onChange={(e) => set('title', e.target.value)}
                error={errors.title}
                maxLength={200}
              />
              <Input
                label="Data"
                type="date"
                value={fields.date}
                onChange={(e) => set('date', e.target.value)}
                error={errors.date}
              />
              <label className="check">
                <input
                  type="checkbox"
                  checked={fields.allDay}
                  onChange={(e) => set('allDay', e.target.checked)}
                />{' '}
                Dia inteiro
              </label>
              {!fields.allDay && (
                <div className="form-row">
                  <Input
                    label="Início"
                    type="time"
                    value={fields.startTime}
                    onChange={(e) => set('startTime', e.target.value)}
                    error={errors.startTime}
                  />
                  <Input
                    label="Fim"
                    optional
                    type="time"
                    value={fields.endTime}
                    onChange={(e) => set('endTime', e.target.value)}
                    hint="Padrão: 1 hora"
                  />
                </div>
              )}
            </>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        {!preset.type && <Button onClick={() => setStage('input')}>Voltar</Button>}
        <Button variant="primary" loading={busy} onClick={save}>
          Salvar
        </Button>
      </ModalFooter>
    </>
  );
}
